import React, { useState } from 'react';
import { Search, Filter, Download, FileText } from 'lucide-react';
import EmptyState from '../EmptyState';
import type { FiscalDocument } from '../../types/fiscal';

interface FiscalListProps {
  documents: FiscalDocument[];
  onSelect: (document: FiscalDocument | null) => void;
}

const FiscalList: React.FC<FiscalListProps> = ({ documents, onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'authorized':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (documents.length === 0) {
    return (
      <EmptyState
        title="Nenhuma nota fiscal emitida"
        description="Comece emitindo sua primeira nota fiscal eletrônica para manter seu negócio em conformidade com a legislação."
        action={
          <button
            onClick={() => onSelect(null)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <FileText className="h-5 w-5 mr-2" />
            Emitir Primeira NF-e
          </button>
        }
      />
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-100">
      {/* Search and Filters */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Buscar por número, cliente ou chave de acesso..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todos os Tipos</option>
              <option value="nfe">NF-e</option>
              <option value="nfce">NFC-e</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todos os Status</option>
              <option value="authorized">Autorizadas</option>
              <option value="pending">Pendentes</option>
              <option value="rejected">Rejeitadas</option>
              <option value="cancelled">Canceladas</option>
            </select>
            <button className="p-2 hover:bg-gray-100 rounded-md">
              <Filter className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Documents List */}
      <div className="divide-y divide-gray-200">
        {documents.map((doc) => (
          <div
            key={doc.id}
            className="p-4 hover:bg-gray-50 cursor-pointer"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-gray-500" />
                  <span className="font-medium">
                    {doc.type === 'nfe' ? 'NF-e' : 'NFC-e'} {doc.number}
                  </span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    getStatusColor(doc.status)
                  }`}>
                    {doc.status === 'authorized' ? 'Autorizada' :
                     doc.status === 'pending' ? 'Pendente' :
                     doc.status === 'rejected' ? 'Rejeitada' : 'Cancelada'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Chave de Acesso: {doc.accessKey}
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Handle XML download
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                  title="Baixar XML"
                >
                  <Download className="h-5 w-5 text-gray-600" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Handle PDF download
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                  title="Baixar PDF"
                >
                  <FileText className="h-5 w-5 text-gray-600" />
                </button>
              </div>
            </div>
            
            <div className="mt-2 grid grid-cols-3 gap-4 text-sm text-gray-500">
              <div>
                <span className="block text-xs text-gray-400">Data de Emissão</span>
                {new Date(doc.issueDate).toLocaleDateString('pt-BR')}
              </div>
              <div>
                <span className="block text-xs text-gray-400">Valor Total</span>
                R$ {doc.totalAmount.toFixed(2)}
              </div>
              <div>
                <span className="block text-xs text-gray-400">Cliente</span>
                {doc.customerId}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FiscalList;